import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { eventSocketClient } from "../utility/EventSocket/EventSocketClient";
import { currentEnv } from "../Api/configs";
import {
  EventSocketState,
  initialEventSocketState,
  StreamEventBroadcast,
  ResultBroadcast,
  VotingEnabledBroadcast,
  VotingCountBroadcast,
  UserRole,
  StreamerRole,
  StreamStatus,
} from "../utility/EventSocket/types";

// ============================================
// Action Types
// ============================================

type EventSocketAction =
  | { type: "SET_CONNECTED"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_EVENT_ID"; payload: string | null }
  | {
      type: "SET_STREAM_STATUS";
      payload: { role: StreamerRole; status: StreamStatus };
    }
  | { type: "SET_RESULT"; payload: ResultBroadcast | null }
  | { type: "SET_VOTING_ENABLED"; payload: boolean }
  | {
      type: "SET_VOTE_COUNT";
      payload: { organiser: number; challenger: number } | null;
    }
  | { type: "RESET" };

// ============================================
// Reducer
// ============================================

function eventSocketReducer(
  state: EventSocketState,
  action: EventSocketAction
): EventSocketState {
  switch (action.type) {
    case "SET_CONNECTED":
      return { ...state, isConnected: action.payload };
    case "SET_ERROR":
      return { ...state, connectionError: action.payload };
    case "SET_EVENT_ID":
      return { ...state, eventId: action.payload };
    case "SET_STREAM_STATUS":
      if (action.payload.role === "organiser") {
        return { ...state, organiserStatus: action.payload.status };
      }
      return { ...state, challengerStatus: action.payload.status };
    case "SET_RESULT":
      return { ...state, result: action.payload };
    case "SET_VOTING_ENABLED":
      return { ...state, votingEnabled: action.payload };
    case "SET_VOTE_COUNT":
      return { ...state, voteCount: action.payload };
    case "RESET":
      return initialEventSocketState;
    default:
      return state;
  }
}

// ============================================
// Context Value Interface
// ============================================

interface EventSocketContextValue extends EventSocketState {
  // Connection methods
  connectToEvent: (
    eventId: string,
    userId: string,
    role: UserRole,
    accessToken?: string
  ) => void;
  disconnectFromEvent: () => void;

  // Fetch initial state from server
  fetchInitialState: (eventId: string) => Promise<void>;

  // Emit methods (for streamers)
  emitStreamStart: (username: string, role: StreamerRole) => void;
  emitStreamDisconnect: (
    username: string,
    role: StreamerRole,
    reason?: string
  ) => void;
  emitStreamReconnect: (username: string, role: StreamerRole) => void;
  emitStreamStop: (username: string, role: StreamerRole) => void;
}

// ============================================
// Context
// ============================================

const EventSocketContext = createContext<EventSocketContextValue | undefined>(
  undefined
);

// ============================================
// Provider
// ============================================

interface EventSocketProviderProps {
  children: ReactNode;
}

export function EventSocketProvider({
  children,
}: EventSocketProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(
    eventSocketReducer,
    initialEventSocketState
  );
  const unsubscribersRef = useRef<(() => void)[]>([]);

  // ============================================
  // Connection Methods
  // ============================================

  const connectToEvent = useCallback(
    (eventId: string, userId: string, role: UserRole, accessToken?: string) => {
      console.log(
        "[EventSocketContext] Connecting to event:",
        eventId,
        "as",
        role
      );
      dispatch({ type: "SET_EVENT_ID", payload: eventId });

      // Connect socket
      eventSocketClient.connect(eventId, userId, role, accessToken);
    },
    []
  );

  const disconnectFromEvent = useCallback(() => {
    console.log("[EventSocketContext] Disconnecting from event");
    eventSocketClient.disconnect();
    dispatch({ type: "RESET" });
  }, []);

  // Fetch initial event state from REST API
  const fetchInitialState = useCallback(
    async (eventId: string): Promise<void> => {
      try {
        const response = await fetch(
          `${currentEnv.eventSocketUrl}/api/emit/state/${eventId}`
        );
        if (!response.ok) {
          console.warn(
            "[EventSocketContext] Failed to fetch initial state:",
            response.status
          );
          return;
        }
        const data = await response.json();
        console.log("[EventSocketContext] Initial state:", data);

        // Update organiser status if available
        if (data.organiser?.status) {
          dispatch({
            type: "SET_STREAM_STATUS",
            payload: { role: "organiser", status: data.organiser.status },
          });
        }

        // Update challenger status if available
        if (data.challenger?.status) {
          dispatch({
            type: "SET_STREAM_STATUS",
            payload: { role: "challenger", status: data.challenger.status },
          });
        }
      } catch (error) {
        console.error(
          "[EventSocketContext] Error fetching initial state:",
          error
        );
      }
    },
    []
  );

  // ============================================
  // Emit Methods (for streamers)
  // ============================================

  const emitStreamStart = useCallback(
    (username: string, role: StreamerRole) => {
      eventSocketClient.emitStreamStart(username, role);
    },
    []
  );

  const emitStreamDisconnect = useCallback(
    (username: string, role: StreamerRole, reason?: string) => {
      eventSocketClient.emitStreamDisconnect(username, role, reason);
    },
    []
  );

  const emitStreamReconnect = useCallback(
    (username: string, role: StreamerRole) => {
      eventSocketClient.emitStreamReconnect(username, role);
    },
    []
  );

  const emitStreamStop = useCallback((username: string, role: StreamerRole) => {
    eventSocketClient.emitStreamStop(username, role);
  }, []);

  // ============================================
  // Event Handlers
  // ============================================

  const handleStreamStarted = useCallback((data: StreamEventBroadcast) => {
    dispatch({
      type: "SET_STREAM_STATUS",
      payload: { role: data.role, status: "live" },
    });
  }, []);

  const handleStreamDisconnected = useCallback((data: StreamEventBroadcast) => {
    dispatch({
      type: "SET_STREAM_STATUS",
      payload: { role: data.role, status: "paused" },
    });
  }, []);

  const handleStreamReconnected = useCallback((data: StreamEventBroadcast) => {
    dispatch({
      type: "SET_STREAM_STATUS",
      payload: { role: data.role, status: "live" },
    });
  }, []);

  const handleStreamStopped = useCallback((data: StreamEventBroadcast) => {
    dispatch({
      type: "SET_STREAM_STATUS",
      payload: { role: data.role, status: "completed" },
    });
  }, []);

  const handleResult = useCallback((data: ResultBroadcast) => {
    dispatch({ type: "SET_RESULT", payload: data });
  }, []);

  const handleVotingEnabled = useCallback((data: VotingEnabledBroadcast) => {
    dispatch({ type: "SET_VOTING_ENABLED", payload: data.publicVotingEnabled });
  }, []);

  const handleVotingCount = useCallback((data: VotingCountBroadcast) => {
    dispatch({ type: "SET_VOTE_COUNT", payload: data.voteCount });
  }, []);

  const handleConnectionChange = useCallback((connected: boolean) => {
    dispatch({ type: "SET_CONNECTED", payload: connected });
    if (!connected) {
      dispatch({ type: "SET_ERROR", payload: "Connection lost" });
    } else {
      dispatch({ type: "SET_ERROR", payload: null });
    }
  }, []);

  const handleError = useCallback((error: string) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  // ============================================
  // Setup Listeners
  // ============================================

  useEffect(() => {
    // Register all listeners
    const unsubscribers = [
      eventSocketClient.onStreamStarted(handleStreamStarted),
      eventSocketClient.onStreamDisconnected(handleStreamDisconnected),
      eventSocketClient.onStreamReconnected(handleStreamReconnected),
      eventSocketClient.onStreamStopped(handleStreamStopped),
      eventSocketClient.onResult(handleResult),
      eventSocketClient.onVotingEnabled(handleVotingEnabled),
      eventSocketClient.onVotingCount(handleVotingCount),
      eventSocketClient.onConnectionChange(handleConnectionChange),
      eventSocketClient.onError(handleError),
    ];
    unsubscribersRef.current = unsubscribers;

    // Cleanup on unmount
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [
    handleStreamStarted,
    handleStreamDisconnected,
    handleStreamReconnected,
    handleStreamStopped,
    handleResult,
    handleVotingEnabled,
    handleVotingCount,
    handleConnectionChange,
    handleError,
  ]);

  // ============================================
  // Context Value
  // ============================================

  const value: EventSocketContextValue = {
    ...state,
    connectToEvent,
    disconnectFromEvent,
    fetchInitialState,
    emitStreamStart,
    emitStreamDisconnect,
    emitStreamReconnect,
    emitStreamStop,
  };

  return (
    <EventSocketContext.Provider value={value}>
      {children}
    </EventSocketContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useEventSocketContext(): EventSocketContextValue {
  const context = useContext(EventSocketContext);
  if (!context) {
    throw new Error(
      "useEventSocketContext must be used within EventSocketProvider"
    );
  }
  return context;
}
